import { useState } from 'react';
import { Button, Flex, Loader, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconArrowLeft } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import type { SmartCreateData } from 'src/secret-menu/smart-create/types';
import { useDeriveFrn } from 'src/secret-menu/smart-create/hooks/useDeriveFrn';
import { useSmartCreateLookup } from 'src/secret-menu/smart-create/hooks/useSmartCreateLookup';

const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;

export default function DetailsFormStep({
  data,
  nextStep,
  canGoBack,
  previousStep,
  goToStepById,
}: StepProps<SmartCreateData>) {
  const { deriveFrn } = useDeriveFrn();
  const { lookup } = useSmartCreateLookup();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      website: data.website,
      trialEndDate: data.trialEndDate ?? new Date(Date.now() + THREE_MONTHS_MS),
    },
    validate: {
      firstName: v => (v.trim().length === 0 ? 'First name is required' : null),
      lastName: v => (v.trim().length === 0 ? 'Last name is required' : null),
      email: v => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Valid email required'),
      website: v => (v.trim().length === 0 ? 'Website is required' : null),
      trialEndDate: v => (v ? null : 'Trial end date is required'),
    },
  });

  const handleSubmit = form.onSubmit(async values => {
    setIsSubmitting(true);
    const baseData: SmartCreateData = { ...data, ...values, frn: '' };

    try {
      const derived = await deriveFrn({ websiteUrl: values.website });
      const frn = derived.data.frn;

      if (frn) {
        try {
          const result = await lookup({ frn });
          const directors = result.data.activeDirectors;
          const isDirector = directors.some(d =>
            d.toLowerCase().includes(values.lastName.toLowerCase()),
          );
          goToStepById('processing', undefined, {
            ...baseData,
            frn,
            companyName: result.data.companyName,
            companyNumber: result.data.companyNumber,
            registeredAddress: result.data.registeredAddress,
            activeDirectors: directors,
            signatoryPosition: isDirector ? 'Director' : '',
          });
          return;
        } catch {
          nextStep({ ...baseData, frn });
          return;
        }
      }
    } catch {
      // network error — fall through to FRN step
    }

    nextStep(baseData);
    setIsSubmitting(false);
  });

  return (
    <Flex direction="column" flex={1} gap="md" mih={0}>
      <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
        Company details
      </Text>

      <Flex direction="column" flex={1} mih={0} style={{ overflowY: 'auto' }}>
        <form id="smart-create-details-form" onSubmit={handleSubmit}>
          <Stack gap="sm">
            <TextInput
              required
              label="Website"
              placeholder="e.g. https://example.com"
              key={form.key('website')}
              {...form.getInputProps('website')}
            />
            <TextInput
              required
              label="First name"
              placeholder="e.g. Jane"
              key={form.key('firstName')}
              {...form.getInputProps('firstName')}
            />
            <TextInput
              required
              label="Last name"
              placeholder="e.g. Smith"
              key={form.key('lastName')}
              {...form.getInputProps('lastName')}
            />
            <TextInput
              required
              label="Email address"
              placeholder="e.g. jane@example.com"
              key={form.key('email')}
              {...form.getInputProps('email')}
            />
            <DateInput
              required
              label="Trial end date"
              placeholder="Pick a date"
              valueFormat="DD MMM YYYY"
              key={form.key('trialEndDate')}
              {...form.getInputProps('trialEndDate')}
            />
          </Stack>
        </form>
      </Flex>

      <Stack gap="sm">
        <Flex justify="space-between" align="center" gap="md">
          {canGoBack && (
            <Button variant="outline-black" size="xl" onClick={() => previousStep()} p="sm">
              <IconArrowLeft size={24} />
            </Button>
          )}
          <Button
            fullWidth
            size="xl"
            type="submit"
            form="smart-create-details-form"
            disabled={isSubmitting}
            leftSection={isSubmitting ? <Loader size="sm" color="gray.4" /> : undefined}
          >
            Continue
          </Button>
        </Flex>
      </Stack>
    </Flex>
  );
}
